import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { cacheable, withTransaction } from '@datorama/akita';
import { AppType, UserAccessRoleType } from '@models/profile/model';
import { GroupPermission, MatchType, QueryCriteria } from '@models/security/model';
import { merge, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  map,
  mapTo,
  mergeMap,
  reduce,
} from 'rxjs';
import { GroupPermissionStore } from 'src/app/state';
import { GroupPermissionModel } from 'src/app/state/model';
import { environment } from 'src/environments/environment';
import { v4 as uuid } from 'uuid';

import { CoreService } from '../models/clux/enum';
import { Uri } from '../utils/uri';
import { BrandService } from './brand.service';
import { BrowserService } from './browser.service';

export type ForgotPasswordError = {
  error: string;
  message: string;
};

export type ApiResult<T> = {
  response?: T;
  error?: ForgotPasswordError;
};

export type ForgotPasswordSuccess = {
  codeDeliveryDetails: {
    attributeName: string;
    deliveryMedium: string;
    destination: string;
  };
};

export type ForgotPasswordResponse = ApiResult<ForgotPasswordSuccess>;
export type ConfirmForgotPasswordResponse = ApiResult<Record<string, never>>;
export type AvailableMethodsResponse = ApiResult<string[]>;

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  private browserService = inject(BrowserService);
  private groupPermissionStore = inject(GroupPermissionStore);
  private http = inject(HttpClient);
  private brandService = inject(BrandService);

  public loadGroupPermissions(groups: UserAccessRoleType[]): Observable<void> {
    if (!groups || !groups.length) {
      return throwError(() => new Error('Contact is not assigned to any roles'));
    }
    const filterCriteria = {
      key: 'id',
      value: '',
      matchType: MatchType.EXACT,
    };
    const criteria: QueryCriteria = {
      index: 'app-index',
      searchCriteria: [
        {
          key: 'app',
          value: 'UBAGUI_CLUX',
          matchType: MatchType.EXACT,
        },
      ],
      filterCriteria: [filterCriteria],
      projectionExpression: 'allowedActions',
    };
    const uri = new Uri('/profile/*/groupPermission/search', CoreService.Security);
    const requests$ = groups.map((group) => {
      const groupCriteria = { ...criteria };
      groupCriteria.filterCriteria = [
        {
          ...filterCriteria,
          value: group,
        },
      ];
      return this.http.post<GroupPermission[]>(uri.toString(), groupCriteria);
    });

    const mergedRequests$ = merge(...requests$).pipe(
      mergeMap((request) => of(request)),
      reduce((allTransactions, subsetTransactions) => allTransactions.concat(subsetTransactions)),
      map((groupPermissions) =>
        groupPermissions.map((groupPermission) => {
          return { ...groupPermission, id: uuid() } as GroupPermissionModel;
        })
      ),
      withTransaction((groupPermissions) => {
        this.groupPermissionStore.set(groupPermissions);
      })
    );

    return cacheable(this.groupPermissionStore, mergedRequests$, { emitNext: true }).pipe(mapTo(null));
  }

  public getEmailFromAuth0Code(code: string, state: string): Observable<string> {
    const origin = this.browserService.getLocationOrigin();
    const pathname = this.browserService.getLocationPathname();
    const options = {
      params: {
        uri: `${origin}${pathname}`,
        resource: AppType.CU,
        client: state,
      },
    };
    const uri = new Uri(`/auth0/code/${code}/email`, CoreService.Security);
    return this.http.get<{ email: string }>(uri.toString(), options).pipe(map((response) => response?.email));
  }

  public initiateNetSuiteSSO(payload: { queryParams: { [key: string]: string }; auth: string }): Observable<string> {
    const uri = new Uri('/netSuiteSSO/oauth2/authorize', CoreService.Security);
    return this.http.post<{ redirect_uri: string }>(uri.toString(), payload).pipe(map((res) => res?.redirect_uri));
  }

  public getAvailableForgotPasswordMethods(username: string): Observable<AvailableMethodsResponse> {
    const uri = new Uri('/forgotPassword', CoreService.Security);
    const body = {
      appClientId: environment.auth.ClientId,
      username,
      tpa: this.brandService.getBrand(),
      method: '',
    };

    return this.http.put<{ availableRecoveryMethods: string[] }>(String(uri), body).pipe(
      map((response) => ({ response: response.availableRecoveryMethods, error: null as ForgotPasswordError | null })),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          return of({
            response: null,
            error: {
              error: error.error.error,
              message: error.error?.message,
            },
          });
        }

        return of({
          response: null,
          error: {
            error: 'Unknown Error',
            message: String(error),
          },
        });
      })
    );
  }

  public forgotPassword(username: string, method: 'email' | 'sms'): Observable<ForgotPasswordResponse> {
    const uri = new Uri('/forgotPassword', CoreService.Security);
    const body = {
      appClientId: environment.auth.ClientId,
      username,
      tpa: this.brandService.getBrand(),
      method,
    };

    return this.http.put<ForgotPasswordSuccess>(String(uri), body).pipe(
      map((response) => ({ response, error: null as ForgotPasswordError | null })),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          return of({
            response: null,
            error: {
              error: error.error.error,
              message: error.error?.message,
            },
          });
        }

        return of({
          response: null,
          error: {
            error: 'Unknown Error',
            message: String(error),
          },
        });
      })
    );
  }

  public confirmForgotPassword(username: string, code: string, newPassword: string): Observable<ConfirmForgotPasswordResponse> {
    const uri = new Uri('/confirmForgotPassword', CoreService.Security);
    const body = {
      appClientId: environment.auth.ClientId,
      username,
      code,
      newPassword,
    };

    return this.http.put<Record<string, never>>(String(uri), body).pipe(
      map((response) => ({ response, error: null as ForgotPasswordError | null })),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          return of({
            response: null,
            error: {
              error: error.error.error,
              message: error.error?.message,
            },
          });
        }

        return of({
          response: null,
          error: {
            error: 'Unknown Error',
            message: String(error),
          },
        });
      })
    );
  }
}
